import {Component, Input, OnChanges, OnDestroy, OnInit, ViewChild, ElementRef} from '@angular/core';
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {Resource} from "../../../model/Resource";
import {
  beginImageRequest,
  endImageRequest,
  HIDDEN_IMAGES_ELEMENT_ID,
  invalidateResourceImages
} from "../../service-ui/resource-image-cache";
import {bestAvailableStage, PdfArtifactStatus, stagedImageId} from "../../service-ui/pdf-artifact-stage";
import {StageWatch} from "../../service-ui/staged-preview-image";
import {Draft} from "../../../model/Draft";
import {ResourceService} from "../../service-data/resource.service";
import {StartupService} from "../../service-ui/startup.service";

@Component({
  selector: 'app-spread',
  templateUrl: './spread.component.html',
  styleUrls: ['./spread.component.css']
})

export class SpreadComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('leftImage', { static: true }) leftImage;
  @ViewChild('rightImage', { static: true }) rightImage;
  @Input() draft: Draft;
  @Input() resourceId: number;
  @Input() leftVerticalShift: number;
  @Input() leftHorizontalShift: number;
  @Input() rightVerticalShift: number;
  @Input() rightHorizontalShift: number;
  @Input() scale: number;
  @Input() leftPageNumber: number;
  @Input() rightPageNumber: number;
  @Input() dpi: number;
  @Input() orgId: number;
  @Input() bodyOrCover: string;
  @Input() bindingType: string;
  @Input() showRuler: boolean;
  @Input() topGui: boolean;
  @Input() showGuideLines: boolean;
  @Input() showDebugGrid: boolean = true; // Show reference grid lines
  @Input() showGutter: boolean = true; // Show spine gutter images
  @Input() unit: 'in' | 'mm' = 'in'; // Unit system for rulers
  @Input() zoom: number = 1.0; // Current zoom level
  @Input() showPageNumbers: boolean = true; // Show page numbers in corners

  leftImageLoaded: boolean = false;
  rightImageLoaded: boolean = false;

  /**
   * True when an interior PDF resource has actually been uploaded. When false
   * the pages show a "No PDF Uploaded Yet" message instead of an indefinite
   * "Loading Image" placeholder.
   */
  get hasResource(): boolean {
    return !!this.resourceId;
  }

  displayScale = 1.0;

  /**
   * Display system operates at 288 pixels per inch
   * - PDF images are at 288 DPI (native resolution)
   * - Display coordinates are at 288 px/inch
   * - Trim size determines the visible page area at 288 px/inch
   */
  private readonly DISPLAY_DPI = 288;
  private readonly POINTS_PER_INCH = 72;

  /** Calculate page display width in pixels at 288 DPI based on trim size */
  get pageDisplayWidth(): number {
    if (this.draft && this.draft.draftBody && this.draft.draftBody.width) {
      const trimWidthInches = this.draft.draftBody.width / this.POINTS_PER_INCH;
      return Math.round(trimWidthInches * this.DISPLAY_DPI);
    }
    return 1152; // fallback: 4 inches at 288 DPI
  }

  /** Calculate page display height in pixels at 288 DPI based on trim size */
  get pageDisplayHeight(): number {
    if (this.draft && this.draft.draftBody && this.draft.draftBody.height) {
      const trimHeightInches = this.draft.draftBody.height / this.POINTS_PER_INCH;
      return Math.round(trimHeightInches * this.DISPLAY_DPI);
    }
    return 1728; // fallback: 6 inches at 288 DPI
  }

  Math = Math;

  // ── margin lines ──────────────────────────────────────────────────

  /** Constant for converting points to pixels at 288 DPI */
  private get pxPerPoint(): number {
    return this.DISPLAY_DPI / this.POINTS_PER_INCH; // 288/72 = 4
  }

  /** Top margin in pixels (converted from points) */
  get marginTopPx(): number {
    if (this.draft && this.draft.draftBody && this.draft.draftBody.marginTop) {
      return Math.round(this.draft.draftBody.marginTop * this.pxPerPoint);
    }
    return 0;
  }

  /** Bottom margin in pixels (converted from points) */
  get marginBottomPx(): number {
    if (this.draft && this.draft.draftBody && this.draft.draftBody.marginBottom) {
      return Math.round(this.draft.draftBody.marginBottom * this.pxPerPoint);
    }
    return 0;
  }

  /** Outside margin in pixels (converted from points) */
  get marginOutPx(): number {
    if (this.draft && this.draft.draftBody && this.draft.draftBody.marginOut) {
      return Math.round(this.draft.draftBody.marginOut * this.pxPerPoint);
    }
    return 0;
  }

  /** Inside (spine) margin in pixels (converted from points) */
  get marginInPx(): number {
    if (this.draft && this.draft.draftBody && this.draft.draftBody.marginIn) {
      return Math.round(this.draft.draftBody.marginIn * this.pxPerPoint);
    }
    return 0;
  }

  // ── dynamic ruler generation ──────────────────────────────────────

  /** Generate tick marks for horizontal ruler (inches) */
  getHorizontalRulerTicksInches(widthPx: number): Array<{position: number, height: number, label?: string}> {
    if (!this.draft || !this.draft.draftBody) {
      return [];
    }

    const trimWidthInches = this.draft.draftBody.width / this.POINTS_PER_INCH;
    const pxPerInch = this.DISPLAY_DPI; // Use base DPI, zoom is applied by browser
    const ticks = [];

    // Determine subdivision based on zoom (for display purposes)
    const effectivePxPerInch = pxPerInch * this.zoom;
    let subdivision = 16; // 1/16th inch
    if (effectivePxPerInch < 72) {
      subdivision = 4; // Only 1/4 inch if zoomed out
    } else if (effectivePxPerInch < 144) {
      subdivision = 8; // 1/8 inch at medium zoom
    }

    for (let i = 0; i <= trimWidthInches * subdivision; i++) {
      const inches = i / subdivision;
      const position = inches * pxPerInch;

      if (position > widthPx) {
        break;
      }

      let height = 4; // Default for finest subdivision
      let label: string;

      if (i % subdivision === 0) {
        // Full inch
        height = 12;
        if (inches > 0) {
          label = this.formatRulerNumber(Math.floor(inches));
        }
      } else if (i % (subdivision / 2) === 0) {
        // Half inch
        height = 8;
      } else if (i % (subdivision / 4) === 0) {
        // Quarter inch
        height = 6;
      }

      ticks.push({ position, height, label });
    }

    return ticks;
  }

  /** Generate tick marks for vertical ruler (inches) */
  getVerticalRulerTicksInches(heightPx: number): Array<{position: number, height: number, label?: string}> {
    if (!this.draft || !this.draft.draftBody) {
      return [];
    }

    const trimHeightInches = this.draft.draftBody.height / this.POINTS_PER_INCH;
    const pxPerInch = this.DISPLAY_DPI; // Use base DPI, zoom is applied by browser
    const ticks = [];

    // Determine subdivision based on zoom (for display purposes)
    const effectivePxPerInch = pxPerInch * this.zoom;
    let subdivision = 16; // 1/16th inch
    if (effectivePxPerInch < 72) {
      subdivision = 4; // Only 1/4 inch if zoomed out
    } else if (effectivePxPerInch < 144) {
      subdivision = 8; // 1/8 inch at medium zoom
    }

    for (let i = 0; i <= trimHeightInches * subdivision; i++) {
      const inches = i / subdivision;
      const position = inches * pxPerInch;

      if (position > heightPx) {
        break;
      }

      let height = 4; // Default for finest subdivision
      let label: string;

      if (i % subdivision === 0) {
        // Full inch
        height = 12;
        if (inches > 0) {
          label = this.formatRulerNumber(Math.floor(inches));
        }
      } else if (i % (subdivision / 2) === 0) {
        // Half inch
        height = 8;
      } else if (i % (subdivision / 4) === 0) {
        // Quarter inch
        height = 6;
      }

      ticks.push({ position, height, label });
    }

    return ticks;
  }

  /** Generate tick marks for horizontal ruler (millimeters) */
  getHorizontalRulerTicksMm(widthPx: number): Array<{position: number, height: number, label?: string}> {
    if (!this.draft || !this.draft.draftBody) {
      return [];
    }

    const trimWidthInches = this.draft.draftBody.width / this.POINTS_PER_INCH;
    const trimWidthMm = trimWidthInches * 25.4;
    const pxPerMm = this.DISPLAY_DPI / 25.4; // Use base DPI, zoom is applied by browser
    const ticks = [];

    // Determine if we show every mm or every 5mm based on zoom
    const effectivePxPerMm = pxPerMm * this.zoom;
    const mmIncrement = (effectivePxPerMm < 2) ? 5 : 1;

    for (let mm = 0; mm <= trimWidthMm; mm += mmIncrement) {
      const position = mm * pxPerMm;

      if (position > widthPx) {
        break;
      }

      let height = 4; // 1mm or 5mm mark
      let label: string;

      if (mm % 10 === 0) {
        // Every cm
        height = 12;
        if (mm > 0) {
          label = this.formatRulerNumber(mm / 10);
        }
      } else if (mm % 5 === 0) {
        // Every 5mm
        height = 8;
      }

      ticks.push({ position, height, label });
    }

    return ticks;
  }

  /** Generate tick marks for vertical ruler (millimeters) */
  getVerticalRulerTicksMm(heightPx: number): Array<{position: number, height: number, label?: string}> {
    if (!this.draft || !this.draft.draftBody) {
      return [];
    }

    const trimHeightInches = this.draft.draftBody.height / this.POINTS_PER_INCH;
    const trimHeightMm = trimHeightInches * 25.4;
    const pxPerMm = this.DISPLAY_DPI / 25.4; // Use base DPI, zoom is applied by browser
    const ticks = [];

    // Determine if we show every mm or every 5mm based on zoom
    const effectivePxPerMm = pxPerMm * this.zoom;
    const mmIncrement = (effectivePxPerMm < 2) ? 5 : 1;

    for (let mm = 0; mm <= trimHeightMm; mm += mmIncrement) {
      const position = mm * pxPerMm;

      if (position > heightPx) {
        break;
      }

      let height = 4; // 1mm or 5mm mark
      let label: string;

      if (mm % 10 === 0) {
        // Every cm
        height = 12;
        if (mm > 0) {
          label = this.formatRulerNumber(mm / 10);
        }
      } else if (mm % 5 === 0) {
        // Every 5mm
        height = 8;
      }

      ticks.push({ position, height, label });
    }

    return ticks;
  }

  // ── debug grid lines ──────────────────────────────────────────────

  /** Get the display pixels per inch for horizontal measurements - always 288 */
  private get displayPPI_horizontal(): number {
    return this.DISPLAY_DPI;
  }

  /** Get the display pixels per inch for vertical measurements - always 288 */
  private get displayPPI_vertical(): number {
    return this.DISPLAY_DPI;
  }

  /**
   * Generate debug grid lines for left (even) page.
   * Lines every 1/4", starting from outside (left) edge.
   * Returns array of { position: px, style: 'solid'|'dotted'|'dashed', axis: 'vertical'|'horizontal' }
   */
  get leftPageDebugLines(): Array<{position: number, style: string, axis: string}> {
    if (!this.draft || !this.draft.draftBody) {
      return [];
    }

    const lines = [];
    const trimWidthInches = this.draft.draftBody.width / 72;
    const trimHeightInches = this.draft.draftBody.height / 72;
    const quarterInchPx = this.displayPPI_horizontal * 0.25;
    const quarterInchPxVert = this.displayPPI_vertical * 0.25;

    // Vertical lines (from left edge = outside edge)
    // Start at 0 (outside edge, which is at left: 9px in the display)
    for (let i = 0; i <= trimWidthInches * 4; i++) {
      const inchOffset = i * 0.25;
      const position = 9 + (i * quarterInchPx);

      let style = 'dotted'; // default for 1/4" increments
      if (i % 4 === 0 && inchOffset >= 1.0) {
        style = 'solid'; // solid at 1" and beyond
      } else if (i % 2 === 0) {
        style = 'dashed'; // dashed at 1/2" increments
      }

      lines.push({ position, style, axis: 'vertical' });
    }

    // Horizontal lines (from top edge)
    for (let i = 0; i <= trimHeightInches * 4; i++) {
      const inchOffset = i * 0.25;
      const position = 9 + (i * quarterInchPxVert);

      let style = 'dotted'; // default for 1/4" increments
      if (i % 4 === 0 && inchOffset >= 1.0) {
        style = 'solid'; // solid at 1" and beyond
      } else if (i % 2 === 0) {
        style = 'dashed'; // dashed at 1/2" increments
      }

      lines.push({ position, style, axis: 'horizontal' });
    }

    return lines;
  }

  /**
   * Generate debug grid lines for right (odd) page.
   * Lines every 1/4", starting from outside (right) edge.
   */
  get rightPageDebugLines(): Array<{position: number, style: string, axis: string}> {
    if (!this.draft || !this.draft.draftBody) {
      return [];
    }

    const lines = [];
    const trimWidthInches = this.draft.draftBody.width / 72;
    const trimHeightInches = this.draft.draftBody.height / 72;
    const quarterInchPx = this.displayPPI_horizontal * 0.25;
    const quarterInchPxVert = this.displayPPI_vertical * 0.25;

    // Vertical lines (from right edge = outside edge)
    // For right page, position from right edge
    for (let i = 0; i <= trimWidthInches * 4; i++) {
      const inchOffset = i * 0.25;
      const position = 9 + (i * quarterInchPx);

      let style = 'dotted'; // default for 1/4" increments
      if (i % 4 === 0 && inchOffset >= 1.0) {
        style = 'solid'; // solid at 1" and beyond
      } else if (i % 2 === 0) {
        style = 'dashed'; // dashed at 1/2" increments
      }

      lines.push({ position, style, axis: 'vertical' });
    }

    // Horizontal lines (from top edge) - same as left page
    for (let i = 0; i <= trimHeightInches * 4; i++) {
      const inchOffset = i * 0.25;
      const position = 9 + (i * quarterInchPxVert);

      let style = 'dotted'; // default for 1/4" increments
      if (i % 4 === 0 && inchOffset >= 1.0) {
        style = 'solid'; // solid at 1" and beyond
      } else if (i % 2 === 0) {
        style = 'dashed'; // dashed at 1/2" increments
      }

      lines.push({ position, style, axis: 'horizontal' });
    }

    return lines;
  }

  constructor (private resourceService: ResourceService, public startupService: StartupService) {
  }

  /** Convert number to Arabic-Indic numerals if in RTL mode */
  formatRulerNumber(num: number): string {
    const str = num.toString();
    // Default to LTR if startupService or dir is not set
    const direction = this.startupService?.dir || 'ltr';
    if (direction !== 'rtl') {
      return str;
    }
    // Arabic-Indic numerals (Eastern Arabic numerals) for RTL
    const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return str.split('').map(char => {
      const digit = parseInt(char, 10);
      if (isNaN(digit)) {
        return char;
      } else {
        return arabicNumerals[digit];
      }
    }).join('');
  }

  /** Check if we're in RTL mode for conditional font sizing */
  get isRTL(): boolean {
    return (this.startupService?.dir || 'ltr') === 'rtl';
  }

  private onDestroySubject: Subject<boolean> = new Subject<boolean>();

  /**
   * Suppresses re-requests on RESOURCE updates that change nothing visible.
   * `updateNotifier` fires for every Resource row, and notifications are
   * broadcast to every session on the role (`shared/Notifications.md` §5), so
   * an unfiltered reaction here is one HTTP call per visible page per
   * unrelated edit.
   */
  private stageWatch = new StageWatch();

  /**
   * Which artefact stage these pages should be showing.
   *
   * Draft side, so `standardized` is deliberately left undefined - a
   * `DraftBody` has no such field, and that absence is what tells
   * `pdf-artifact-stage` that a PREPARED render is already final here. Adding
   * a `standardized: ''` would label every finished Draft preliminary forever.
   */
  private artifactStatus(): PdfArtifactStatus {
    const resourceId = (this.draft && this.draft.draftBody && this.draft.draftBody.resource_ID) || 0;
    const resource = resourceId ? this.resourceService.getLocalItem(resourceId) : null;
    return { resourceId: resourceId, prepared: resource ? resource.prepared : undefined };
  }

  ngOnInit() {
    this.refreshImagePositions();

    // The RESOURCE UPDATE notification is the only signal that a PDF which
    // could not be rendered a moment ago now can. It is applied by
    // `AbstractLiveDataService.addUpdateLocalItem`, which mutates the cached
    // Resource IN PLACE - so the `draft` @Input never changes identity and
    // `ngOnChanges` never fires. Without this subscription the body pages
    // stayed on "Loading Image" until the user reloaded.
    this.resourceService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe((resource: Resource) => this.onResourceUpdated(resource));
  }

  ngOnDestroy() {
    this.onDestroySubject.next(true);
    this.onDestroySubject.complete();
  }

  /**
   * A Resource changed. If it is the one backing these pages, re-render.
   *
   * `numPages` is adopted from the Resource when the DraftBody does not have
   * it yet. The page gate below is `pageNumber > draftBody.numPages`, and
   * that field is written by a separate row update which may not have
   * reached us - so without this the spread would refuse to draw page 1 of a
   * PDF the server has just told us has pages. It is an in-memory read-
   * through only: nothing is PUT, and the authoritative DraftBody value
   * overwrites it when it arrives.
   */
  private onResourceUpdated(resource: Resource): void {
    if ( !resource || !resource.getId || !this.draft || !this.draft.draftBody ) {
      return;
    }
    const showing = this.draft.draftBody.resource_ID;
    if ( !showing || resource.getId() !== showing ) {
      return;
    }
    if ( !this.draft.draftBody.numPages && resource.numPages > 0 ) {
      this.draft.draftBody.numPages = resource.numPages;
    }
    // The cached element id carries the artefact stage, so an ORIGINAL and a
    // PREPARED render of the same page are different entries and the better
    // one arrives simply by asking for it. Invalidation is therefore only
    // needed for what the id cannot express - the same stage's bytes changing,
    // i.e. a re-upload against this Resource id - so it is gated on a real
    // change rather than run on every notification.
    if ( this.stageWatch.changed(this.artifactStatus()) ) {
      invalidateResourceImages(showing);
    }
    this.refreshImagePositions();
  }

  ngOnChanges() {
    this.refreshImagePositions();
  }

  refreshImagePositionDetails(cachedImage: any, image: any, horizontalShift: number, verticalShift: number, isEvenPage: boolean) {
    image.nativeElement.src = cachedImage.src;

    if (!this.draft || !this.draft.draftBody) {
      return;
    }

    // CRITICAL: Everything operates at 288 pixels per inch
    // - PDF page images are at 288 DPI (e.g., 5.5"×8.5" = 1584×2448 pixels)
    // - Display coordinate system is at 288 px/inch
    // - Shifts are in points (1/72 inch) and must be converted to pixels at 288 DPI

    const PX_PER_INCH = 288;
    const POINTS_PER_INCH = 72;
    const PX_PER_POINT = PX_PER_INCH / POINTS_PER_INCH; // 288/72 = 4 pixels per point

    // Convert shifts from points to pixels at 288 DPI
    const hShiftPx = horizontalShift * PX_PER_POINT;
    const vShiftPx = verticalShift * PX_PER_POINT;

    // The 9px offset accounts for danger zone (8px) + border (1px)
    // Trim lines are 9px inset from the container edges
    const trimLineOffset = 9;

    if (isEvenPage) {
      // Left page:
      // - Container has 9px border on all sides
      // - Spine (inner edge) is at right edge of trim area = 9px from right edge of container
      // - With zero shift, image right edge should be at spine position (9px from container right)
      // - Horizontal shift: positive moves image leftward (away from spine)
      image.nativeElement.style.left = '';
      image.nativeElement.style.right = (trimLineOffset - hShiftPx) + 'px';
    } else {
      // Right page:
      // - Container has 9px border on all sides
      // - Spine (inner edge) is at left edge of trim area = 9px from left edge of container
      // - With zero shift, image left edge should be at spine position (9px from container left)
      // - Horizontal shift: positive moves image rightward (away from spine)
      image.nativeElement.style.right = '';
      image.nativeElement.style.left = (trimLineOffset + hShiftPx) + 'px';
    }

    // Vertical positioning:
    // - Top trim line is 9px from container top
    // - With zero shift, image top should align with top trim line
    // - Positive shift moves content UP (decreases top), negative moves DOWN (increases top)
    image.nativeElement.style.top = (trimLineOffset - vShiftPx) + 'px';

    // Mark image as loaded to hide "Loading Image" text
    if (isEvenPage) {
      this.leftImageLoaded = true;
    } else {
      this.rightImageLoaded = true;
    }
  }

  refreshImagePositions() {
    this.leftImageLoaded = false;
    this.rightImageLoaded = false;
    this.refreshImagePosition(this.leftPageNumber, this.leftImage, this.leftHorizontalShift, this.leftVerticalShift, true);
    this.refreshImagePosition(this.rightPageNumber, this.rightImage, this.rightHorizontalShift, this.rightVerticalShift, false);
  }
  refreshImagePosition(pageNumber: number, image: any, horizontalShift: number, verticalShift: number, isEvenPage: boolean) {
    if ( !this.draft || !this.draft.draftBody ) {
      return;
    }
    this.resourceId = this.draft.draftBody.resource_ID;

    // A DraftBody with no Resource yet - `resource_ID` 0, or absent because
    // the server sent null - has no pages to render. Without this guard every
    // refresh issued `GET /resource/imageurl/0/...` for BOTH pages of the
    // spread, and `refreshImagePositions()` re-runs on each change, so an
    // empty Draft produced a steady stream of failing requests and left the
    // previous Draft's page showing.
    //
    // The guard that used to live here was commented out rather than fixed,
    // and the reason is visible in what it did: it wrote to `this.leftImage`
    // and `this.rightImage` directly. Those are @ViewChild refs that are
    // undefined until the view initialises, so it threw on the first call.
    // This version clears only the element it was handed - the same one the
    // out-of-range `pageNumber` branch below clears - and tolerates a ref
    // that is not bound yet.
    if ( !this.resourceId ) {
      if ( image && image.nativeElement ) {
        image.nativeElement.src = '';
        image.nativeElement.style.display = 'none';
        const holder = image.nativeElement.parentElement && image.nativeElement.parentElement.parentElement;
        if ( holder ) {
          holder.style.background = 'url("assets/images/noPageTile.png") repeat';
        }
      }
      // Nothing will ever load, so retire the "Loading Image" placeholder for
      // this side. Leaving the flag false is what would otherwise leave that
      // text on screen permanently - it is rendered on `!leftImageLoaded` /
      // `!rightImageLoaded`, which nothing else would ever set.
      if ( isEvenPage ) {
        this.leftImageLoaded = true;
      } else {
        this.rightImageLoaded = true;
      }
      return;
    }

    // ⚠ The stage suffix is what makes an improved render actually reach the
    // screen. Without it, the ORIGINAL image cached before preparation
    // finished is found again afterwards, read as a hit, and the better
    // PREPARED render is never requested. It also means we do not depend on
    // the server returning a different signed URL for the two versions -
    // `shared/PdfArtifactImages.md` Q3, still unanswered.
    const imageId = stagedImageId('resource-' + this.resourceId + '-' + pageNumber + '-' + this.dpi,
      bestAvailableStage(this.artifactStatus()));

    if ( pageNumber === 0 || pageNumber > this.draft.draftBody.numPages ) {
      image.nativeElement.src = '';
      // image.nativeElement.style.background = 'url("images/noPageTile.png") repeat';
      image.nativeElement.style.display = 'none';
      image.nativeElement.parentElement.parentElement.style.background = 'url("assets/images/noPageTile.png") repeat';
    } else {
      let cachedImage: any = document.getElementById(imageId);
      if ( !cachedImage ) {
        const hiddenImagesElement: any = document.getElementById(HIDDEN_IMAGES_ELEMENT_ID);
        // `beginImageRequest` replaces what a cached placeholder used to do.
        // refreshImagePositions() runs from ngOnChanges, i.e. on every
        // change-detection pass, so an unguarded request here is a request
        // per tick - but caching a src-less <img> to prevent that made the
        // miss permanent, because every later pass then took it as a hit.
        if ( hiddenImagesElement && beginImageRequest(imageId) ) {
          const imageElement = document.createElement("img");
          console.log(`Getting image for resourceId: ${this.resourceId} 'COLOR' ${this.dpi} ${pageNumber}`);
          this.resourceService.getImageURL(this.resourceId, this.draft.interiorType, this.dpi, pageNumber).subscribe(imageURL => {
            endImageRequest(imageId);
            if ( imageURL ) {
              console.log(`GOT     image for resourceId: ${this.resourceId} 'COLOR' ${this.dpi} ${pageNumber} ${imageURL}`);
              imageElement.src = imageURL;
              imageElement.id = imageId;
              hiddenImagesElement.appendChild(imageElement);
              cachedImage = imageElement;
              if (cachedImage.complete ) {
                this.refreshImagePositionDetails(cachedImage, image, horizontalShift, verticalShift, isEvenPage);
              } else {
                cachedImage.addEventListener('load', _ => {
                  this.refreshImagePositionDetails(cachedImage, image, horizontalShift, verticalShift, isEvenPage);
                });
                cachedImage.addEventListener('error', _ => {
                  console.log(`Error loading image ${imageId}`);
                });
              }
            } else {
              // Normal before the PDF is processed. Nothing is cached, so the
              // RESOURCE UPDATE will produce a real retry - except for a
              // serviceProvider session, which never receives one, so it
              // re-reads the row instead. No-ops for every other role.
              this.resourceService.ensureProcessedResource(this.resourceId);
              console.log(`No image yet for resourceId: ${this.resourceId} ${this.dpi} ${pageNumber}`);
            }
          }, err => {
            endImageRequest(imageId);
            console.log(`Error requesting image ${imageId}`, err);
          });
        }
      }
      if ( cachedImage ) {
        if (cachedImage.complete ) {
          this.refreshImagePositionDetails(cachedImage, image, horizontalShift, verticalShift, isEvenPage);
        } else {
          cachedImage.addEventListener('load', _ => {
            this.refreshImagePositionDetails(cachedImage, image, horizontalShift, verticalShift, isEvenPage);
          });
          cachedImage.addEventListener('error', _ => {
            console.log(`Error loading image ${imageId}`);
          });
        }
      }
    }
  }
}


